import { useEffect, useState } from 'react';
import { Eye, EyeOff, Copy } from 'lucide-react';
import { ActionBar, Container, PageHeader, Section } from '@vnetwork/ui/layout';
import { Button, IconButton, Input, Switch, Skeleton } from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { useToast } from '@vnetwork/ui/feedback';
import {
  useVodProvisioningConfig,
  useUpdateVodProvisioningConfig,
} from '@/api/media/vod-provisioning';
import type {
  VodProvisioningConfig,
  UpdateVodProvisioningPayload,
} from '@/api/media/vod-provisioning-types';
import { VodConsoleBreadcrumbs } from '../VodConsoleBreadcrumbs';
import styles from './ProvisioningPage.module.css';

// ─── Form state ───────────────────────────────────────────────────────────────

type ProvForm = UpdateVodProvisioningPayload;

function configToForm(c: VodProvisioningConfig): ProvForm {
  return {
    jitt_token: c.jitt_token ?? '',
    hls_dash: {
      config_id: c.hls_dash?.config_id ?? 0,
      hls_fragment: c.hls_dash?.hls_fragment ?? 6,
      dash_fragment: c.hls_dash?.dash_fragment ?? 6,
    },
    manifest: {
      vms_manifest_name: c.manifest?.vms_manifest_name ?? '',
    },
    thumbnail: {
      id: String(c.thumbnail?.thumbnail_id ?? ''),
      interval: c.thumbnail?.interval ?? 10,
      img_in_sprite_num: c.thumbnail?.img_in_sprite_num ?? 100,
      width: c.thumbnail?.width ?? 160,
      height: c.thumbnail?.height ?? 90,
      cell_width: c.thumbnail?.cell_width ?? 160,
      cell_height: c.thumbnail?.cell_height ?? 90,
      cell_margin: c.thumbnail?.cell_margin ?? 0,
      cell_padding: c.thumbnail?.cell_padding ?? 0,
      use_sprite: c.thumbnail?.use_sprite ?? false,
    },
  };
}

type FormErrors = Partial<Record<string, string>>;

function validateForm(form: ProvForm): FormErrors {
  const errs: FormErrors = {};
  if (!form.jitt_token?.trim()) errs.jitt_token = 'JWT token is required.';
  if (form.hls_dash.hls_fragment <= 0) errs.hls_fragment = 'Must be > 0.';
  if (form.hls_dash.dash_fragment <= 0) errs.dash_fragment = 'Must be > 0.';
  if (!form.manifest.vms_manifest_name?.trim()) errs.manifest_name = 'Manifest name is required.';
  if (form.thumbnail.interval <= 0) errs.interval = 'Must be > 0.';
  if (form.thumbnail.img_in_sprite_num <= 0) errs.img_in_sprite_num = 'Must be > 0.';
  if (form.thumbnail.width <= 0) errs.width = 'Must be > 0.';
  if (form.thumbnail.height <= 0) errs.height = 'Must be > 0.';
  if (form.thumbnail.cell_width <= 0) errs.cell_width = 'Must be > 0.';
  if (form.thumbnail.cell_height <= 0) errs.cell_height = 'Must be > 0.';
  return errs;
}

// ─── Secret input (reveal / copy) ────────────────────────────────────────────

interface SecretInputProps {
  value: string;
  onChange: (v: string) => void;
  onCopy?: () => void;
  // Field render props forwarded for label association
  id?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean | 'true' | 'false';
}

function SecretInput({
  value,
  onChange,
  onCopy,
  id,
  'aria-describedby': ariaDescribedby,
  'aria-invalid': ariaInvalid,
}: SecretInputProps) {
  const [revealed, setRevealed] = useState(false);
  return (
    <div className={styles.secretInput}>
      <div className={styles.secretInputField}>
        <Input
          id={id}
          aria-describedby={ariaDescribedby}
          aria-invalid={ariaInvalid}
          type={revealed ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="••••••••"
        />
      </div>
      <IconButton size="sm" aria-label={revealed ? 'Hide token' : 'Reveal token'} onClick={() => setRevealed((p) => !p)}>
        {revealed ? <EyeOff size={14} /> : <Eye size={14} />}
      </IconButton>
      {onCopy && (
        <IconButton size="sm" aria-label="Copy token" onClick={onCopy}>
          <Copy size={14} />
        </IconButton>
      )}
    </div>
  );
}

// ─── Number + unit field ──────────────────────────────────────────────────────

interface NumberWithUnitProps {
  value: number;
  unit: string;
  invalid?: boolean;
  onChange: (v: number) => void;
  // Field render props forwarded for label association
  id?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean | 'true' | 'false';
}

function NumberWithUnit({
  value,
  unit,
  invalid,
  onChange,
  ...fieldProps
}: NumberWithUnitProps) {
  return (
    <Input
      type="number"
      value={value}
      invalid={invalid}
      endSlot={<span className={styles.unitLabel}>{unit}</span>}
      onChange={(e) => onChange(Number(e.target.value))}
      {...fieldProps}
    />
  );
}

// ─── Main provisioning page ───────────────────────────────────────────────────

export function VodProvisioning({ serviceId }: { serviceId: string }) {
  const toast = useToast();

  const { data: config, isLoading, error, refetch } = useVodProvisioningConfig(serviceId);
  const updateMutation = useUpdateVodProvisioningConfig(serviceId);

  const [form, setForm] = useState<ProvForm | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});

  // Sync config → form on first load
  useEffect(() => {
    if (config && !form) {
      setForm(configToForm(config));
      setIsDirty(false);
    }
  }, [config, form]);

  const patch = (updater: (prev: ProvForm) => ProvForm) => {
    setForm((prev) => {
      if (!prev) return prev;
      const next = updater(prev);
      setIsDirty(true);
      return next;
    });
    // Clear validation errors so Save re-enables after the user corrects a field
    setErrors({});
  };

  const reset = () => {
    if (config) { setForm(configToForm(config)); setIsDirty(false); setErrors({}); }
  };

  const save = () => {
    if (!form) return;
    const errs = validateForm(form);
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setErrors({});
    updateMutation.mutate(form, {
      onSuccess: () => { toast.success('Provisioning config saved'); setIsDirty(false); refetch(); },
      onError: (err) => toast.error((err as Error).message || 'Failed to save provisioning config'),
    });
  };

  const copyToken = () => {
    if (form?.jitt_token) { navigator.clipboard.writeText(form.jitt_token); toast.success('Token copied'); }
  };

  if (error) {
    return (
      <Container size="xl">
        <PageHeader
          breadcrumbs={<VodConsoleBreadcrumbs trail={[{ label: 'Provisioning' }]} />}
          title="Provisioning"
          description="Token, streaming fragments, manifest and thumbnail settings."
        />
        <div className={styles.errorBox}>{(error as Error).message || 'Failed to load provisioning config.'}</div>
      </Container>
    );
  }

  if (isLoading || !form) {
    return (
      <Container size="xl">
        <PageHeader
          breadcrumbs={<VodConsoleBreadcrumbs trail={[{ label: 'Provisioning' }]} />}
          title="Provisioning"
          description="Token, streaming fragments, manifest and thumbnail settings."
        />
        <div className={styles.skeletonGrid}>
          <Skeleton variant="rect" height={160} />
          <Skeleton variant="rect" height={160} />
        </div>
      </Container>
    );
  }

  return (
    <Container size="xl">
      <PageHeader
        breadcrumbs={<VodConsoleBreadcrumbs trail={[{ label: 'Provisioning' }]} />}
        title="Provisioning"
        description="Token, streaming fragments, manifest and thumbnail settings."
      />
      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      <Section title="General" description="Base authentication token for provisioning requests." style={{ marginBottom: 0 }}>
        <Field label="JWT Token" required error={errors.jitt_token}>
          {(fp) => (
            <SecretInput
              {...fp}
              value={form.jitt_token}
              onChange={(v) => patch((p) => ({ ...p, jitt_token: v }))}
              onCopy={copyToken}
            />
          )}
        </Field>
      </Section>

      <Section title="HLS / DASH" description="Fragment duration for adaptive bitrate outputs." style={{ marginBottom: 0 }}>
        <div className={styles.grid2}>
          <Field label="HLS Fragment" required error={errors.hls_fragment}>
            {(fp) => (
              <NumberWithUnit
                {...fp}
                value={form.hls_dash.hls_fragment}
                unit="sec"
                invalid={!!errors.hls_fragment}
                onChange={(v) => patch((prev) => ({ ...prev, hls_dash: { ...prev.hls_dash, hls_fragment: v } }))}
              />
            )}
          </Field>
          <Field label="DASH Fragment" required error={errors.dash_fragment}>
            {(fp) => (
              <NumberWithUnit
                {...fp}
                value={form.hls_dash.dash_fragment}
                unit="sec"
                invalid={!!errors.dash_fragment}
                onChange={(v) => patch((prev) => ({ ...prev, hls_dash: { ...prev.hls_dash, dash_fragment: v } }))}
              />
            )}
          </Field>
        </div>
      </Section>

      <Section title="Manifest" description="Naming rules used for playback endpoints." style={{ marginBottom: 0 }}>
        <Field label="VMS Manifest Name" required error={errors.manifest_name}>
          {(p) => (
            <Input
              {...p}
              placeholder="e.g. index.m3u8"
              value={form.manifest.vms_manifest_name}
              invalid={!!errors.manifest_name}
              onChange={(e) => patch((prev) => ({ ...prev, manifest: { ...prev.manifest, vms_manifest_name: e.target.value } }))}
            />
          )}
        </Field>
      </Section>

      <Section title="Thumbnail" description="Sprite image generation settings." style={{ marginBottom: 0 }}>
        <p className={styles.subSectionTitle}>Basic</p>
        <div className={styles.grid3}>
          <Field label="Interval" required error={errors.interval}>
            {(fp) => (
              <NumberWithUnit
                {...fp}
                value={form.thumbnail.interval}
                unit="sec"
                invalid={!!errors.interval}
                onChange={(v) => patch((prev) => ({ ...prev, thumbnail: { ...prev.thumbnail, interval: v } }))}
              />
            )}
          </Field>
          <Field label="Images in Sprite" required error={errors.img_in_sprite_num}>
            {(fp) => (
              <NumberWithUnit
                {...fp}
                value={form.thumbnail.img_in_sprite_num}
                unit="img"
                invalid={!!errors.img_in_sprite_num}
                onChange={(v) => patch((prev) => ({ ...prev, thumbnail: { ...prev.thumbnail, img_in_sprite_num: v } }))}
              />
            )}
          </Field>
          <div className={styles.switchRow}>
            <Switch
              id="vod-use-sprite"
              checked={form.thumbnail.use_sprite}
              onCheckedChange={(checked) =>
                patch((prev) => ({ ...prev, thumbnail: { ...prev.thumbnail, use_sprite: checked } }))
              }
            />
            <label htmlFor="vod-use-sprite">Use sprite</label>
          </div>
        </div>

        <p className={styles.subSectionTitle}>Image Size</p>
        <div className={styles.grid2}>
          <Field label="Width" required error={errors.width}>
            {(fp) => (
              <NumberWithUnit
                {...fp}
                value={form.thumbnail.width}
                unit="px"
                invalid={!!errors.width}
                onChange={(v) => patch((prev) => ({ ...prev, thumbnail: { ...prev.thumbnail, width: v } }))}
              />
            )}
          </Field>
          <Field label="Height" required error={errors.height}>
            {(fp) => (
              <NumberWithUnit
                {...fp}
                value={form.thumbnail.height}
                unit="px"
                invalid={!!errors.height}
                onChange={(v) => patch((prev) => ({ ...prev, thumbnail: { ...prev.thumbnail, height: v } }))}
              />
            )}
          </Field>
        </div>

        <p className={styles.subSectionTitle}>Sprite Cell</p>
        <div className={styles.grid4}>
          <Field label="Cell Width" required error={errors.cell_width}>
            {(fp) => (
              <NumberWithUnit
                {...fp}
                value={form.thumbnail.cell_width}
                unit="px"
                invalid={!!errors.cell_width}
                onChange={(v) => patch((prev) => ({ ...prev, thumbnail: { ...prev.thumbnail, cell_width: v } }))}
              />
            )}
          </Field>
          <Field label="Cell Height" required error={errors.cell_height}>
            {(fp) => (
              <NumberWithUnit
                {...fp}
                value={form.thumbnail.cell_height}
                unit="px"
                invalid={!!errors.cell_height}
                onChange={(v) => patch((prev) => ({ ...prev, thumbnail: { ...prev.thumbnail, cell_height: v } }))}
              />
            )}
          </Field>
          <Field label="Cell Margin">
            {(fp) => (
              <NumberWithUnit
                {...fp}
                value={form.thumbnail.cell_margin}
                unit="px"
                onChange={(v) => patch((prev) => ({ ...prev, thumbnail: { ...prev.thumbnail, cell_margin: v } }))}
              />
            )}
          </Field>
          <Field label="Cell Padding">
            {(fp) => (
              <NumberWithUnit
                {...fp}
                value={form.thumbnail.cell_padding}
                unit="px"
                onChange={(v) => patch((prev) => ({ ...prev, thumbnail: { ...prev.thumbnail, cell_padding: v } }))}
              />
            )}
          </Field>
        </div>
      </Section>

      <ActionBar
        start={
          isDirty ? (
            <span className={styles.dirtyBanner}>You have unsaved changes.</span>
          ) : undefined
        }
        end={
          <>
            <Button
              variant="secondary"
              disabled={!isDirty || updateMutation.isPending}
              onClick={reset}
            >
              Reset
            </Button>
            <Button
              variant="primary"
              disabled={updateMutation.isPending || !isDirty || Object.keys(errors).length > 0}
              onClick={save}
            >
              {updateMutation.isPending ? 'Saving…' : 'Save Changes'}
            </Button>
          </>
        }
      />
      </div>
    </Container>
  );
}
